export class Lightbox extends HTMLElement {
    static observedAttributes = ["open"];

    constructor () {
        super();

        this.selectors = {
            classes: {
                modal: { close: 'lightbox-modal-close' },
                swiper: { wrapper: 'swiper-wrapper' }
            },
            attributes: {
                lightbox: {
                    trigger: 'lightbox-trigger',
                    close: 'lightbox-close'
                }
            }
        };

        this.lightboxSwiper = null;
        this.zoomState = {
            scale: 1,
            translateX: 0,
            translateY: 0,
            isDragging: false,
            startX: 0,
            startY: 0,
            lastX: 0,
            lastY: 0
        };
    }

    init() {
        this.addEventListener('click', this.handleEvent.bind(this));
    }

    connectedCallback() { this.init(); }

    disconnectedCallback() {
        if (this.lightboxSwiper) {
            this.lightboxSwiper.destroy();
            this.lightboxSwiper = null;
        }
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'open') {
            this.setAttribute('aria-hidden', newValue !== null ? 'false' : 'true');
        }
    }

    showLightbox() {
        this.setAttribute('open', '');
        document.documentElement.style.overflow = 'hidden';
        document.documentElement.style.maxHeight = '100vh';
    }

    closeLightbox() {
        this.removeAttribute('open');
        document.documentElement.style.overflow = '';
        document.documentElement.style.maxHeight = '';
        this.remove();
    }

    createSwiperSlide(src) {
        const slideNode = document.createElement('div');
        slideNode.classList.add('swiper-slide', 'lightbox-slide');

        const zoomNode = document.createElement('div');
        zoomNode.classList.add('swiper-zoom-container');

        let formattedSource = new URL(src, window.location.origin);
        if (formattedSource.searchParams.get('width') < 2000) {
            formattedSource.searchParams.set('width', 2000);
        }

        const slideImg = new Image();
        slideImg.src = formattedSource.href;
        slideImg.style.cursor = 'url("https://cdn.shopify.com/s/files/1/0687/6499/1765/files/Zoom_In_Cursor.svg"), auto';
        slideImg.dataset.originalSrc = src;
        this.addZoomPanListeners(slideImg, zoomNode);

        zoomNode.appendChild(slideImg);
        slideNode.appendChild(zoomNode);

        return slideNode;
    }

    createThumbnail(src, index) {
        const thumbNode = document.createElement('div');
        thumbNode.classList.add('lightbox-thumbnail');
        thumbNode.dataset.index = index;
        thumbNode.dataset.src = src;

        let thumbSource = new URL(src, window.location.origin);
        thumbSource.searchParams.set('width', 150);

        const thumbImg = new Image();
        thumbImg.src = thumbSource.href;
        thumbImg.alt = `Thumbnail ${index + 1}`;
        thumbImg.loading = 'lazy';
        thumbNode.appendChild(thumbImg);

        thumbNode.addEventListener('click', () => {
            if (this.lightboxSwiper) this.lightboxSwiper.slideTo(index);
        });

        return thumbNode;
    }

    addZoomPanListeners(img, container) {
        // Check if mobile device
        const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth < 768;
        
        // On mobile, allow native zoom - no custom listeners needed
        if (isMobile) {
            img.style.cursor = 'default';
            img.style.touchAction = 'auto'; // Allow native pinch-zoom
            return; // Exit early, no custom zoom on mobile
        }
        
        // Desktop-only custom zoom below
        let scale = 1;
        let translateX = 0;
        let translateY = 0;
        let isDragging = false;
        let startX = 0;
        let startY = 0;
        let dragMoved = false;

        const updateTransform = () => {
            img.style.transform = `scale(${scale}) translate(${translateX}px, ${translateY}px)`;
            if (isDragging) {
                img.style.cursor = 'grabbing';
            } else {
                img.style.cursor = scale > 1 ? 'url("https://cdn.shopify.com/s/files/1/0687/6499/1765/files/Zoom_out_cursor.svg?v=1761566361"), auto' : 'url("https://cdn.shopify.com/s/files/1/0687/6499/1765/files/Zoom_In_Cursor.svg"), auto';
            }
        };

        // Desktop click-to-zoom
        img.addEventListener('click', (e) => {
            if (!dragMoved) {
                e.preventDefault();
                e.stopPropagation();

                if (scale === 1) {
                    scale = 3;
                    if (this.lightboxSwiper) this.lightboxSwiper.allowTouchMove = false;

                    const rect = img.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const y = e.clientY - rect.top;

                    translateX = (rect.width / 2 - x) / scale;
                    translateY = (rect.height / 2 - y) / scale;

                    img.style.cursor = 'url("https://cdn.shopify.com/s/files/1/0687/6499/1765/files/Zoom_out_cursor.svg?v=1761566361"), auto';
                } else {
                    scale = 1;
                    translateX = 0;
                    translateY = 0;

                    if (this.lightboxSwiper) this.lightboxSwiper.allowTouchMove = true;
                    img.style.cursor = 'url("https://cdn.shopify.com/s/files/1/0687/6499/1765/files/Zoom_In_Cursor.svg"), auto';
                }

                img.style.transition = 'transform 0.3s ease';
                updateTransform();
                setTimeout(() => { img.style.transition = ''; }, 300);
            }
            dragMoved = false;
        });

        // Desktop drag functionality
        const startDrag = (clientX, clientY) => {
            if (scale > 1) {
                isDragging = true;
                dragMoved = false;
                startX = clientX;
                startY = clientY;
                img.style.cursor = 'grabbing';
            }
        };

        const moveDrag = (clientX, clientY) => {
            if (!isDragging || scale <= 1) return;

            const deltaX = clientX - startX;
            const deltaY = clientY - startY;

            if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) dragMoved = true;

            translateX += deltaX / scale;
            translateY += deltaY / scale;

            const rect = img.getBoundingClientRect();
            const maxX = (rect.width * (scale - 1)) / (2 * scale);
            const maxY = (rect.height * (scale - 1)) / (2 * scale);
            translateX = Math.max(-maxX, Math.min(maxX, translateX));
            translateY = Math.max(-maxY, Math.min(maxY, translateY));

            startX = clientX;
            startY = clientY;

            updateTransform();
        };

        const endDrag = () => {
            if (isDragging) {
                isDragging = false;
                img.style.cursor = scale > 1 ? 'url("https://cdn.shopify.com/s/files/1/0687/6499/1765/files/Zoom_out_cursor.svg?v=1761566361"), auto' : 'url("https://cdn.shopify.com/s/files/1/0687/6499/1765/files/Zoom_In_Cursor.svg"), auto';
            }
        };

        // Mouse drag (desktop only)
        img.addEventListener('mousedown', (e) => {
            if (scale > 1) {
                e.preventDefault();
                e.stopPropagation();
                startDrag(e.clientX, e.clientY);
                const onMove = (ev) => moveDrag(ev.clientX, ev.clientY);
                const onUp = () => {
                    endDrag();
                    document.removeEventListener('mousemove', onMove);
                    document.removeEventListener('mouseup', onUp);
                };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
            }
        });

        // Reset zoom function
        const resetZoom = () => {
            scale = 1;
            translateX = 0;
            translateY = 0;
            isDragging = false;
            img.style.transition = 'transform 0.3s ease';
            updateTransform();
            setTimeout(() => { img.style.transition = ''; }, 300);
            if (this.lightboxSwiper) this.lightboxSwiper.allowTouchMove = true;
            img.style.cursor = 'url("https://cdn.shopify.com/s/files/1/0687/6499/1765/files/Zoom_In_Cursor.svg"), auto';
        };

        img.resetZoom = resetZoom;
    }

    initSwiper() {
        this.lightboxSwiper = new window.Swiper(this, {
            slidesPerView: 1,
            spaceBetween: 500,
            keyboard: { enabled: true, onlyInViewport: false },
            on: {
                afterInit: () => {
                    this.removeAttribute('modal-loading');
                    this.updateThumbnailActive(0);
                },
                slideChange: () => {
                    const slides = this.querySelectorAll('.lightbox-slide img');
                    slides.forEach(img => { if (img.resetZoom) img.resetZoom(); });
                    this.updateThumbnailActive(this.lightboxSwiper.activeIndex);
                }
            }
        });
    }

    updateThumbnailActive(activeIndex) {
        const thumbs = this.querySelectorAll('.lightbox-thumbnail');
        thumbs.forEach((thumb, i) => {
            thumb.classList.toggle('active', i === activeIndex);
        });
    }

    handleEvent(e) {
        if (e.target.closest(".lightbox-close").hasAttribute(this.selectors.attributes.lightbox.close)) {
            this.closeLightbox();
        }
    }

    showPinchOverlay() {
        if (!/Mobi|Android/i.test(navigator.userAgent)) return; // Only mobile

        const overlay = new Image();
        overlay.src = 'https://cdn.shopify.com/s/files/1/0687/6499/1765/files/pinch.svg';
        overlay.classList.add('pinch-overlay');
        overlay.style.position = 'absolute';
        overlay.style.top = '50%';
        overlay.style.left = '50%';
        overlay.style.transform = 'translate(-50%, -50%)';
        overlay.style.pointerEvents = 'none';
        overlay.style.zIndex = '1000';
        overlay.style.transition = 'opacity 0.3s ease';
        overlay.style.opacity = '1';
        overlay.style.width = '126px';
        overlay.style.height = 'auto';

        this.appendChild(overlay);

        const removeOverlay = () => {
            overlay.style.opacity = '0';
            setTimeout(() => overlay.remove(), 300);
            this.removeEventListener('touchstart', removeOverlay, { capture: true });
            this.removeEventListener('mousedown', removeOverlay, { capture: true });
        };

        this.addEventListener('touchstart', removeOverlay, { capture: true, once: true });
        this.addEventListener('mousedown', removeOverlay, { capture: true, once: true });
    }

    showClickOverlay() {
        
        if (/Mobi|Android/i.test(navigator.userAgent)) return; // Only desktop

        const overlay = new Image();
        overlay.src = 'https://cdn.shopify.com/s/files/1/0687/6499/1765/files/Desktop_Zoom_Icon_1.svg'; // Replace with your click-to-zoom icon
        overlay.classList.add('click-overlay');
        overlay.style.position = 'absolute';
        overlay.style.top = '50%';
        overlay.style.left = '50%';
        overlay.style.transform = 'translate(-50%, -50%)';
        overlay.style.pointerEvents = 'none';
        overlay.style.zIndex = '1000';
        overlay.style.transition = 'opacity 0.3s ease';
        overlay.style.opacity = '1';
        overlay.style.width = '126px';
        overlay.style.height = 'auto';

        this.appendChild(overlay);

        const removeOverlay = () => {
            overlay.style.opacity = '0';
            setTimeout(() => overlay.remove(), 300);
            this.removeEventListener('click', removeOverlay, { capture: true });
        };

        // Remove overlay on first click
        this.addEventListener('click', removeOverlay, { capture: true, once: true });
    }

    openLightbox(trigger) {
        const group = trigger.dataset.lightboxGroup;
        const src = trigger.dataset.lightboxSrc;
        const sources = this.getLightboxSources(group, src);
        this.loadSwiperSlides(sources, src);
        this.showLightbox();

        // Show appropriate overlay based on device
        this.showPinchOverlay();
        this.showClickOverlay();
    }

    getLightboxSources(group, src) {
        let sources = [];
        if (group) {
            const groupTriggers = document.querySelectorAll(`[lightbox-trigger][data-lightbox-group="${group}"]`);
            groupTriggers.forEach(t => {
                const s = t.dataset.lightboxSrc;
                if (s) sources.push(s);
            });
        } else {
            sources.push(src);
        }
        return sources;
    }

    loadSwiperSlides(sources, src) {
        const slides = [];
        const thumbs = [];
        const wrapper = this.querySelector(`.${this.selectors.classes.swiper.wrapper}`);
        wrapper.innerHTML = '';
        let thumbContainer = this.querySelector('.lightbox-thumbnails');
        if (!thumbContainer) {
            thumbContainer = document.createElement('div');
            thumbContainer.classList.add('lightbox-thumbnails');
            this.appendChild(thumbContainer);
        } else {
            thumbContainer.innerHTML = '';
        }

        let selectedIndex = 0;
        sources.forEach((s, i) => {
            slides.push(this.createSwiperSlide(s));
            thumbs.push(this.createThumbnail(s, i));
            if (s === src) selectedIndex = i;
        });

        wrapper.append(...slides);
        thumbContainer.append(...thumbs);

        if (!this.lightboxSwiper) this.initSwiper();
        this.lightboxSwiper.update();
        this.lightboxSwiper.slideTo(selectedIndex, 0, true);
    }
}

if (!customElements.get('lightbox-modal')) {
    customElements.define('lightbox-modal', Lightbox);

    async function createLightbox(trigger) {
        return fetch(window.Shopify.routes.root + "?section_id=qf-lightbox")
        .then(res => {
            if (!res.ok) throw new Error("Network response was not ok");
            return res.text();
        })
        .then(htmlString => {
            const html = new DOMParser().parseFromString(htmlString, 'text/html');
            const lightboxEl = html.querySelector('lightbox-modal');
            if (lightboxEl) {
                document.body.appendChild(lightboxEl);
                lightboxEl.openLightbox(trigger);
            }
        })
        .catch(err => {
            console.error("Error:", err);
            alert("Failed to load lightbox. Please try again.");
        });
    }

    document.body.addEventListener('click', (e) => {
        const trigger = e.target.closest('[lightbox-trigger]');
        if (trigger) {
            trigger.disabled = true;
            createLightbox(trigger).then(() => trigger.disabled = false);
        }
    });
}